import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';
import './AdminDashboard.css';

const PORTAL = '/ft-portal';

/* ── Horizontal bar chart (area charts) ── */
const AREA_COLORS  = ['#1043A0','#CF142B','#0891b2','#7c3aed','#d97706','#10b981','#0A1F4E','#db2777','#065f46','#92400e'];
const PIE_COLORS   = ['#CF142B','#1043A0','#d97706','#10b981','#7c3aed','#0891b2','#0A1F4E'];
const PACKAGE_COLORS = { trial:'#6366f1', bronze:'#d97706', silver:'#64748b', gold:'#eab308' };

function HBarChart({ rows = [], valueKey = 'count', labelKey = 'area', valuePrefix = '', colorSet = AREA_COLORS }) {
  if (!rows.length) return <p className="adm-empty-msg">No data yet</p>;
  const max = Math.max(...rows.map(r => Number(r[valueKey]) || 0), 1);
  return (
    <div className="adm-hbar-list">
      {rows.map((r, i) => {
        const val = Number(r[valueKey]) || 0;
        const pct = Math.max((val / max) * 100, 1);
        return (
          <div key={i} className="adm-hbar">
            <span className="adm-hbar-label" title={r[labelKey]}>{r[labelKey]}</span>
            <div className="adm-hbar-track">
              <div className="adm-hbar-fill" style={{ width: `${pct}%`, background: colorSet[i % colorSet.length] }} />
            </div>
            <span className="adm-hbar-val">{valuePrefix}{val}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Toggle bar chart (revenue / new students) ── */
function ToggleBarChart({ data = {}, periods = [], valueKey = 'total', labelKey = 'label', valuePrefix = '', barColor = '#1043A0' }) {
  const [period, setPeriod] = useState(periods[0] || 'daily');
  const rows = data[period] || [];
  const max  = Math.max(...rows.map(r => Number(r[valueKey]) || 0), 1);
  return (
    <div>
      <div className="adm-period-tabs">
        {periods.map(p => (
          <button key={p} className={`adm-period-btn${period === p ? ' adm-period-btn--active' : ''}`} onClick={() => setPeriod(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="adm-empty-msg">No data for this period</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div className="adm-vbar-wrap" style={{ minWidth: `${Math.max(rows.length * 44, 220)}px` }}>
            <div className="adm-vbar-chart">
              {rows.map((r, i) => {
                const val = Number(r[valueKey]) || 0;
                const pct = Math.max((val / max) * 100, 2);
                return (
                  <div key={i} className="adm-vbar-col">
                    <span className="adm-vbar-value">{valuePrefix}{Number.isInteger(val) ? val : val.toFixed(0)}</span>
                    <div className="adm-vbar-bar" style={{ height: `${pct}%`, background: barColor }} title={`${r[labelKey]}: ${valuePrefix}${val}`} />
                    <span className="adm-vbar-label">{r[labelKey]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SVG Line Chart ── */
function LineChart({ data = {}, periods = [], valueKey = 'total', labelKey = 'label', valuePrefix = '', lineColor = '#1043A0' }) {
  const [period, setPeriod] = useState(periods[0] || 'daily');
  const [hovered, setHovered] = useState(null);
  const rows = data[period] || [];

  if (!rows.length) return (
    <div>
      <div className="adm-period-tabs">
        {periods.map(p => (
          <button key={p} className={`adm-period-btn${period === p ? ' adm-period-btn--active' : ''}`} onClick={() => setPeriod(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      <p className="adm-empty-msg">No data for this period</p>
    </div>
  );

  const values = rows.map(r => Number(r[valueKey]) || 0);
  const max = Math.max(...values, 1);
  const W = 360, H = 150;
  const PAD = { top: 18, right: 16, bottom: 36, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const n = rows.length;
  const xOf = (i) => PAD.left + (n <= 1 ? chartW / 2 : (i / (n - 1)) * chartW);
  const yOf = (v) => PAD.top + chartH - Math.max((v / max) * chartH, 0);

  const pts = rows.map((r, i) => ({ x: xOf(i), y: yOf(Number(r[valueKey]) || 0), val: Number(r[valueKey]) || 0, lbl: r[labelKey] }));
  const linePath = `M ${pts.map(p => `${p.x},${p.y}`).join(' L ')}` ;
  const areaPath = `M ${PAD.left},${PAD.top + chartH} L ${pts.map(p => `${p.x},${p.y}`).join(' L ')} L ${PAD.left + chartW},${PAD.top + chartH} Z`;

  // Y-axis guide lines
  const yTicks = [0, 0.5, 1].map(t => ({ v: max * t, y: yOf(max * t) }));

  return (
    <div>
      <div className="adm-period-tabs">
        {periods.map(p => (
          <button key={p} className={`adm-period-btn${period === p ? ' adm-period-btn--active' : ''}`} onClick={() => { setPeriod(p); setHovered(null); }}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${Math.max(W, n * 44 + PAD.left + PAD.right)} ${H}`}
          style={{ width: '100%', minWidth: Math.max(W, n * 44 + PAD.left + PAD.right), height: H, display: 'block' }}
          onMouseLeave={() => setHovered(null)}
        >
          {/* Y-axis guide lines */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={t.y} x2={PAD.left + chartW} y2={t.y} stroke="#f1f5f9" strokeWidth={1} />
              <text x={PAD.left - 6} y={t.y + 4} fontSize={9} fill="#94a3b8" textAnchor="end">
                {valuePrefix}{t.v >= 1000 ? `${(t.v/1000).toFixed(1)}k` : Math.round(t.v)}
              </text>
            </g>
          ))}

          {/* Gradient fill under line */}
          <defs>
            <linearGradient id={`lg-${lineColor.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity="0.18" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#lg-${lineColor.replace('#','')})`} />

          {/* Line */}
          <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={lineColor} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />

          {/* Dots + hover areas */}
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={hovered === i ? 6 : 4}
                fill={hovered === i ? lineColor : '#fff'}
                stroke={lineColor} strokeWidth={2}
                style={{ transition: 'r 0.12s' }}
                onMouseEnter={() => setHovered(i)}
              />
              {/* Hover tooltip */}
              {hovered === i && (
                <g>
                  <rect x={p.x - 30} y={p.y - 28} width={60} height={20} rx={4} fill={lineColor} />
                  <text x={p.x} y={p.y - 14} textAnchor="middle" fontSize={10} fill="#fff" fontWeight={700}>
                    {valuePrefix}{Number.isInteger(p.val) ? p.val : p.val.toFixed(0)}
                  </text>
                </g>
              )}
              {/* X-axis labels — show every Nth if crowded */}
              {(n <= 12 || i % Math.ceil(n / 12) === 0) && (
                <text x={p.x} y={H - 4} textAnchor="middle" fontSize={8.5} fill="#94a3b8">{p.lbl}</text>
              )}
            </g>
          ))}

          {/* X + Y axis lines */}
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#e2e8f0" strokeWidth={1} />
          <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH} stroke="#e2e8f0" strokeWidth={1} />
        </svg>
      </div>
    </div>
  );
}

/* ── SVG Donut pie chart ── */
function PieChart({ data = [] }) {
  const [hovered, setHovered] = useState(null);
  const total = data.reduce((s, d) => s + (Number(d.count) || 0), 0);
  if (!data.length || total === 0) return <p className="adm-empty-msg">No package data yet</p>;

  const cx = 80, cy = 80, r = 65, ri = 38;
  let cursor = -90;
  const slices = data.map((d, i) => {
    const pct  = (Number(d.count) || 0) / total;
    const deg  = pct * 360;
    const color = PACKAGE_COLORS[d.package?.toLowerCase()] || PIE_COLORS[i % PIE_COLORS.length];
    const s = (cursor * Math.PI) / 180;
    const e = ((cursor + deg) * Math.PI) / 180;
    const path = [
      `M ${cx + r*Math.cos(s)} ${cy + r*Math.sin(s)}`,
      `A ${r} ${r} 0 ${deg > 180 ? 1 : 0} 1 ${cx + r*Math.cos(e)} ${cy + r*Math.sin(e)}`,
      `L ${cx + ri*Math.cos(e)} ${cy + ri*Math.sin(e)}`,
      `A ${ri} ${ri} 0 ${deg > 180 ? 1 : 0} 0 ${cx + ri*Math.cos(s)} ${cy + ri*Math.sin(s)} Z`
    ].join(' ');
    cursor += deg;
    return { path, color, pct: Math.round(pct * 100), label: d.package, count: d.count };
  });

  return (
    <div className="adm-pie-wrap">
      <svg width="160" height="160" viewBox="0 0 160 160" style={{ flexShrink: 0 }}>
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth={hovered === i ? 3 : 1.5}
            style={{ transform: hovered === i ? 'scale(1.05)' : 'scale(1)', transformOrigin: '80px 80px', transition: 'transform 0.15s', cursor: 'pointer', opacity: hovered !== null && hovered !== i ? 0.6 : 1 }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} />
        ))}
        <text x="80" y="76" textAnchor="middle" fontSize="18" fontWeight="800" fill="#0A1F4E">{total}</text>
        <text x="80" y="91" textAnchor="middle" fontSize="9" fill="#94a3b8" fontWeight="600">TOTAL</text>
      </svg>
      <div className="adm-pie-legend">
        {slices.map((s, i) => (
          <div key={i} className="adm-pie-leg-item"
            style={{ opacity: hovered !== null && hovered !== i ? 0.5 : 1, cursor: 'default' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            <span className="adm-pie-dot" style={{ background: s.color }} />
            <span className="adm-pie-leg-label">{s.label ? s.label.charAt(0).toUpperCase() + s.label.slice(1) : 'Other'}</span>
            <span className="adm-pie-leg-pct">{s.pct}%</span>
            <span className="adm-pie-leg-count">({s.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Simple CSS bar chart ── */
function BarChart({ bars, maxVal, height = 80 }) {
  if (!bars || bars.length === 0) return null;
  const mx = maxVal || Math.max(...bars.map(b => b.value), 1);
  return (
    <div className="adm-chart" style={{ height }}>
      {bars.map((b, i) => (
        <div key={i} className="adm-chart-col">
          <div
            className="adm-chart-bar"
            style={{ height: `${Math.max((b.value / mx) * 100, 2)}%`, background: b.color || '#1043A0' }}
            title={`${b.label}: ${b.value}`}
          />
          <span className="adm-chart-label">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Mini donut / ring stat ── */
function RingChart({ pct, color = '#1043A0', size = 64 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={7} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={7}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
    </svg>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/dashboard');
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Dashboard">
        <div className="ap-loading"><div className="ap-spinner" /><br />Loading dashboard…</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout pageTitle="Dashboard">
        <div className="ap-empty">
          <p>{error}</p>
          <button onClick={fetchStats} className="ap-btn ap-btn--primary">Retry</button>
        </div>
      </AdminLayout>
    );
  }

  const appTotal  = stats?.applications?.total  || 0;
  const appPend   = stats?.applications?.pending  || 0;
  const appAppr   = stats?.applications?.approved || 0;
  const appRej    = stats?.applications?.rejected || 0;
  const instrTot  = stats?.instructors?.total   || 0;
  const instrAct  = stats?.instructors?.active  || 0;
  const studTot   = stats?.students?.total      || 0;
  const studAct   = stats?.students?.active     || 0;
  const reqTot    = stats?.studentRequests?.total || 0;
  const reqPend   = stats?.studentRequests?.pending  || 0;
  const reqAssign = stats?.studentRequests?.assigned || 0;
  const refTot    = stats?.referrals?.total      || 0;
  const revenue   = ((stats?.payments?.total_revenue || 0) / 100).toFixed(2);
  const txns      = stats?.payments?.total_transactions || 0;

  const approvedPct = appTotal > 0 ? Math.round((appAppr / appTotal) * 100) : 0;
  const activePct   = instrTot > 0 ? Math.round((instrAct / instrTot) * 100) : 0;

  // Chart data from backend
  const charts = stats?.charts || {};

  return (
    <AdminLayout pageTitle="Dashboard">

      {/* ── KPI cards ── */}
      <div className="adm-kpi-grid">

        <div className="adm-kpi adm-kpi--blue" onClick={() => navigate(`${PORTAL}/applications`)} style={{cursor:'pointer'}}>
          <div className="adm-kpi-top">
            <span className="adm-kpi-label">Instructor Requests</span>
            <span className="adm-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </span>
          </div>
          <div className="adm-kpi-value">{appPend}</div>
          <div className="adm-kpi-sub">
            <span className="adm-kpi-dot adm-kpi-dot--amber">{appPend} pending</span>
            <span className="adm-kpi-dot adm-kpi-dot--green">{appAppr} approved</span>
          </div>
          <div className="adm-kpi-chart">
            <BarChart
              bars={[
                { label: 'Pend', value: appPend,  color: '#f59e0b' },
                { label: 'Appr', value: appAppr,  color: '#10b981' },
                { label: 'Rej',  value: appRej,   color: '#ef4444' },
              ]}
              height={56}
            />
          </div>
        </div>

        <div className="adm-kpi adm-kpi--navy" onClick={() => navigate(`${PORTAL}/instructors`)} style={{cursor:'pointer'}}>
          <div className="adm-kpi-top">
            <span className="adm-kpi-label">Instructors</span>
            <span className="adm-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
          </div>
          <div className="adm-kpi-value">{instrTot}</div>
          <div className="adm-kpi-sub">
            <span className="adm-kpi-dot adm-kpi-dot--green">{instrAct} active</span>
            <span className="adm-kpi-dot adm-kpi-dot--grey">{instrTot - instrAct} inactive</span>
          </div>
          <div className="adm-kpi-ring-wrap">
            <RingChart pct={activePct} color="#CF142B" />
            <span className="adm-kpi-ring-label">{activePct}% active</span>
          </div>
        </div>

        <div className="adm-kpi adm-kpi--purple" onClick={() => navigate(`${PORTAL}/student-requests`)} style={{cursor:'pointer'}}>
          <div className="adm-kpi-top">
            <span className="adm-kpi-label">Student Requests</span>
            <span className="adm-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
              </svg>
            </span>
          </div>
          <div className="adm-kpi-value">{reqPend}</div>
          <div className="adm-kpi-sub">
            <span className="adm-kpi-dot adm-kpi-dot--amber">{reqPend} pending</span>
            <span className="adm-kpi-dot adm-kpi-dot--blue">{reqAssign} assigned</span>
          </div>
        </div>

                <div className="adm-kpi adm-kpi--teal" onClick={() => navigate(`${PORTAL}/students`)} style={{cursor:'pointer'}}>
          <div className="adm-kpi-top">
            <span className="adm-kpi-label">Students</span>
            <span className="adm-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
          </div>
          <div className="adm-kpi-value">{studTot}</div>
          <div className="adm-kpi-sub">
            <span className="adm-kpi-dot adm-kpi-dot--green">{studAct || studTot} active</span>
          </div>
        </div>

        <div className="adm-kpi adm-kpi--red" onClick={() => navigate(`${PORTAL}/revenue`)} style={{cursor:'pointer'}}>
          <div className="adm-kpi-top">
            <span className="adm-kpi-label">Total Revenue</span>
            <span className="adm-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </span>
          </div>
          <div className="adm-kpi-value adm-kpi-value--sm">£{revenue}</div>
          <div className="adm-kpi-sub">
            <span className="adm-kpi-dot adm-kpi-dot--green">{txns} transactions</span>
          </div>
        </div>

        <div className="adm-kpi adm-kpi--gold" onClick={() => navigate(`${PORTAL}/referrals`)} style={{cursor:'pointer'}}>
          <div className="adm-kpi-top">
            <span className="adm-kpi-label">Referrals</span>
            <span className="adm-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/>
                <line x1="12" y1="22" x2="12" y2="7"/>
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            </span>
          </div>
          <div className="adm-kpi-value">{refTot}</div>
          <div className="adm-kpi-sub">
            <span className="adm-kpi-dot adm-kpi-dot--amber">{stats?.referrals?.pending || 0} pending</span>
            <span className="adm-kpi-dot adm-kpi-dot--green">£{stats?.referrals?.total_paid || 0} paid</span>
          </div>
        </div>

      </div>

      {/* ── Application status chart ── */}
      <div className="adm-row">
        <div className="ap-panel adm-panel--grow">
          <p className="ap-panel-title">Application Breakdown</p>
          <div className="adm-app-bars">
            {[
              { label: 'Pending',      val: appPend,                     color: '#f59e0b' },
              { label: 'Under Review', val: stats?.applications?.under_review || 0, color: '#1043A0' },
              { label: 'Approved',     val: appAppr,                     color: '#10b981' },
              { label: 'Rejected',     val: appRej,                      color: '#ef4444' },
            ].map(({ label, val, color }) => {
              const pct = appTotal > 0 ? Math.max((val / appTotal) * 100, 0) : 0;
              return (
                <div key={label} className="adm-hbar">
                  <span className="adm-hbar-label">{label}</span>
                  <div className="adm-hbar-track">
                    <div className="adm-hbar-fill" style={{ width: `${pct}%`, background: color }} />
                  </div>
                  <span className="adm-hbar-val">{val}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Quick actions ── */}
        <div className="ap-panel adm-panel--actions">
          <p className="ap-panel-title">Quick Actions</p>
          <div className="adm-actions">
            {[
              { svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, label: 'Pending Applications', path: `${PORTAL}/applications` },
              { svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>, label: 'Assign Students', path: `${PORTAL}/student-requests` },
              { svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>, label: 'Edit Terms', path: `${PORTAL}/cms/terms` },
              { svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, label: 'Edit Privacy', path: `${PORTAL}/cms/privacy` },
              { svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4"/><path d="M21 12c0 1.2-.504 2.3-1.35 3.15l-5.8 5.85a2.12 2.12 0 0 1-3 0l-5.8-5.85A4.5 4.5 0 0 1 3 12V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7z"/></svg>, label: 'Instructor Terms', path: `${PORTAL}/cms/instructor-terms` },
              { svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>, label: 'System Settings', path: `${PORTAL}/settings` },
            ].map(a => (
              <button key={a.label} className="adm-action-btn" onClick={() => navigate(a.path)}>
                <span>{a.svg}</span> {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════
          CHARTS — Row 1: Demand + Supply (Geographic)
      ════════════════════════════════════════════ */}
      <div className="adm-section-title">📍 Geographic Insights</div>
      <div className="adm-row adm-row--equal">

        {/* Chart 1 — Student demand by test centre */}
        <div className="ap-panel adm-panel--grow">
          <div className="adm-chart-header">
            <p className="ap-panel-title" style={{ margin: 0, border: 0, padding: 0 }}>Student Demand by Test Centre</p>
            <span className="adm-chart-badge adm-chart-badge--red">Top 10 areas</span>
          </div>
          <HBarChart rows={charts.testCentreDemand || []} valueKey="count" labelKey="area" colorSet={AREA_COLORS} />
        </div>

        {/* Chart 3 — Instructor supply by test centre */}
        <div className="ap-panel adm-panel--grow">
          <div className="adm-chart-header">
            <p className="ap-panel-title" style={{ margin: 0, border: 0, padding: 0 }}>Instructor Supply by Test Centre</p>
            <span className="adm-chart-badge adm-chart-badge--navy">Active only</span>
          </div>
          <HBarChart
            rows={charts.instructorsByArea || []}
            valueKey="count"
            labelKey="area"
            colorSet={['#0A1F4E','#1043A0','#1d4ed8','#2563eb','#3b82f6','#60a5fa','#093e8f','#0c2d6b','#0e2252','#092047']}
          />
        </div>

      </div>

      {/* ════════════════════════════════════════════
          CHARTS — Row 2: Revenue + New Students (Trends)
      ════════════════════════════════════════════ */}
      <div className="adm-section-title">📈 Growth Trends</div>
      <div className="adm-row adm-row--equal">

        {/* Chart 2 — Revenue */}
        <div className="ap-panel adm-panel--grow">
          <div className="adm-chart-header">
            <p className="ap-panel-title" style={{ margin: 0, border: 0, padding: 0 }}>Revenue</p>
            <span className="adm-chart-badge adm-chart-badge--green">£ GBP</span>
          </div>
          <LineChart
            data={charts.revenue || {}}
            periods={['daily', 'weekly', 'monthly', 'yearly']}
            valueKey="total"
            labelKey="label"
            valuePrefix="£"
            lineColor="#CF142B"
          />
        </div>

        {/* Chart 4 — New student requests */}
        <div className="ap-panel adm-panel--grow">
          <div className="adm-chart-header">
            <p className="ap-panel-title" style={{ margin: 0, border: 0, padding: 0 }}>New Student Requests</p>
            <span className="adm-chart-badge adm-chart-badge--purple">Bookings</span>
          </div>
          <LineChart
            data={charts.newStudents || {}}
            periods={['daily', 'weekly', 'monthly']}
            valueKey="count"
            labelKey="label"
            lineColor="#7c3aed"
          />
        </div>

      </div>

      {/* ════════════════════════════════════════════
          CHARTS — Row 3: Package Popularity (Pie only)
      ════════════════════════════════════════════ */}
      <div className="adm-section-title">🎯 Package Performance</div>
      <div className="adm-row">

        {/* Chart 5 — Package pie */}
        <div className="ap-panel" style={{ maxWidth: 460 }}>
          <div className="adm-chart-header">
            <p className="ap-panel-title" style={{ margin: 0, border: 0, padding: 0 }}>Package Popularity</p>
            <span className="adm-chart-badge adm-chart-badge--gold">All time</span>
          </div>
          <PieChart data={charts.packageStats || []} />
        </div>

      </div>

      {/* ── Recent lists ── */}
      <div className="adm-recent-grid">

        <div className="ap-panel">
          <div className="adm-panel-hdr">
            <p className="ap-panel-title" style={{margin:0,border:0,padding:0}}>Recent Applications</p>
            <button className="ap-btn ap-btn--outline ap-btn--sm" onClick={() => navigate(`${PORTAL}/applications`)}>
              View all →
            </button>
          </div>
          <div className="adm-list">
            {stats?.recent_applications?.length > 0 ? stats.recent_applications.map(app => (
              <div key={app.id} className="adm-list-item" onClick={() => navigate(`${PORTAL}/applications`)}>
                <div className="adm-list-avatar">
                  {(app.full_name || app.first_name || '?')[0].toUpperCase()}
                </div>
                <div className="adm-list-info">
                  <strong>{app.full_name || `${app.first_name || ''} ${app.last_name || ''}`.trim()}</strong>
                  <span>{app.email}</span>
                </div>
                <span className={`ap-badge ap-badge--${app.status === 'under_review' ? 'active' : app.status}`}>
                  {app.status?.replace('_', ' ')}
                </span>
              </div>
            )) : <p className="adm-empty-msg">No applications yet</p>}
          </div>
        </div>

        <div className="ap-panel">
          <div className="adm-panel-hdr">
            <p className="ap-panel-title" style={{margin:0,border:0,padding:0}}>Student Requests</p>
            <button className="ap-btn ap-btn--outline ap-btn--sm" onClick={() => navigate(`${PORTAL}/student-requests`)}>
              View all →
            </button>
          </div>
          <div className="adm-list">
            {stats?.recent_student_requests?.length > 0 ? stats.recent_student_requests.map(req => (
              <div key={req.id} className="adm-list-item" onClick={() => navigate(`${PORTAL}/student-requests`)}>
                <div className="adm-list-avatar adm-list-avatar--purple">
                  {(req.full_name || '?')[0].toUpperCase()}
                </div>
                <div className="adm-list-info">
                  <strong>{req.full_name}</strong>
                  <span>{req.test_centre} — {req.transmission}</span>
                </div>
                <span className={`ap-badge ap-badge--${req.status}`}>{req.status}</span>
              </div>
            )) : <p className="adm-empty-msg">No requests yet</p>}
          </div>
        </div>

        <div className="ap-panel">
          <div className="adm-panel-hdr">
            <p className="ap-panel-title" style={{margin:0,border:0,padding:0}}>Recent Payments</p>
            <button className="ap-btn ap-btn--outline ap-btn--sm" onClick={() => navigate(`${PORTAL}/revenue`)}>
              View all →
            </button>
          </div>
          <div className="adm-list">
            {stats?.recent_payments?.length > 0 ? stats.recent_payments.map(pmt => (
              <div key={pmt.id} className="adm-list-item">
                <div className="adm-list-avatar adm-list-avatar--green">£</div>
                <div className="adm-list-info">
                  <strong>£{(pmt.amount / 100).toFixed(2)}</strong>
                  <span>{pmt.purpose || pmt.customer_name}</span>
                </div>
                <span className={`ap-badge ap-badge--${pmt.status}`}>{pmt.status}</span>
              </div>
            )) : <p className="adm-empty-msg">No payments yet</p>}
          </div>
        </div>

      </div>

    </AdminLayout>
  );
};

export default AdminDashboard;
